import type { ContractRestorePreview } from "@closer/shared";
import { Button, Checkbox, Modal, Skeleton, Textarea } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * ARŞİVDEN GERİ ALMA ONAYI (CLOS-123 `[105]`).
 *
 * ### ⚠ Hedef durum GÖSTERİLİYOR
 *
 * Geri alma bir "aktifleştir" düğmesi değil: sözleşme arşivlenmeden ÖNCEKİ durumuna
 * dönüyor ve bu durum hukuki bir anlam taşıyor. Kullanıcı nereye döneceğini görmeden
 * onaylamamalı.
 *
 * ### ⚠ Kapasite ENGELİ önceden söyleniyor
 *
 * Kapasite yoksa düğme sunulmuyor ve sebebi yazıyor. Basınca hata alan bir düğme,
 * kullanıcıyı "neden olmadı?" sorusuyla baş başa bırakırdı.
 *
 * ### ⚠ Workflow durumu uyarısı onay kutucuğuyla
 *
 * Kart: *"eski açık iş veya geçersiz workflow durumu SESSİZCE canlandırılmaz."* Durum
 * yeniden yazılıyor ama süreç geri gelmiyor; kullanıcı bunu bilerek onaylıyor.
 */

export function ContractRestoreDialog({
  contractId,
  open,
  onClose,
  onConfirm,
  isPending,
}: {
  contractId: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (girdi: { reason: string; acknowledgeWarnings: boolean }) => void;
  isPending: boolean;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const [reason, setReason] = useState("");
  const [onaylandi, setOnaylandi] = useState(false);

  const sorgu = useQuery({
    enabled: open,
    queryKey: tenantKey(tenantId, "contract", contractId, "geri-alma-onizleme"),
    queryFn: async () =>
      (
        await api<ContractRestorePreview>(
          `/api/contracts/${contractId}/lifecycle/restore-preview`,
          { tenantId },
        )
      ).data,
  });

  const onizleme = sorgu.data;
  const uyariVar = (onizleme?.warnings.length ?? 0) > 0;
  const gonderilebilir = Boolean(onizleme?.canRestore) && (!uyariVar || onaylandi);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Arşivden geri al"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          {onizleme?.canRestore && (
            <Button
              type="button"
              loading={isPending}
              disabled={!gonderilebilir}
              onClick={() => onConfirm({ reason: reason.trim(), acknowledgeWarnings: onaylandi })}
            >
              Geri al
            </Button>
          )}
        </div>
      }
    >
      {sorgu.isPending ? (
        <div role="status" aria-label="Geri alma bilgileri yükleniyor">
          <Skeleton lines={4} />
        </div>
      ) : sorgu.error || !onizleme ? (
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true })}
          onRetry={() => void sorgu.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {onizleme.targetLabel && (
            <p className="text-sm text-fg">
              Sözleşme <span className="font-medium">{onizleme.targetLabel}</span> durumuna dönecek.
            </p>
          )}

          {onizleme.blocking.length > 0 && (
            <section className="flex flex-col gap-1">
              <h3 className="text-sm font-semibold text-danger">Geri alınamıyor</h3>
              <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-fg">
                {onizleme.blocking.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </section>
          )}

          {uyariVar && (
            <section className="flex flex-col gap-1">
              <h3 className="text-sm font-semibold text-warning">Dikkat</h3>
              <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-fg">
                {onizleme.warnings.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </section>
          )}

          <section className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-fg-heading">Geri alınca ne olur?</h3>
            <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-fg-soft">
              {onizleme.effects.map((etki) => (
                <li key={etki}>{etki}</li>
              ))}
            </ul>
          </section>

          {onizleme.canRestore && (
            <>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-fg-soft">Geri alma nedeni (isteğe bağlı)</span>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder="Örn. Müvekkil süreci yeniden başlattı"
                />
              </label>

              {uyariVar && (
                <Checkbox
                  checked={onaylandi}
                  onChange={(e) => setOnaylandi(e.target.checked)}
                  label="Sürecin kendiliğinden devam etmeyeceğini anladım"
                />
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
